/**
 * The other end of an invitation link.
 *
 * Everything on this page is in service of one thing: the person holding the
 * link is standing behind a counter, probably on a phone, and has never seen
 * CenoX before. So it tells them who invited them and what they are being
 * given *before* asking for anything, asks for the least it can — a name and a
 * password — and finishes by putting them inside the business rather than at a
 * sign-in screen they have just earned the right to skip.
 *
 * The email is not a field. It was fixed when the invitation was issued, and
 * letting the holder of a link choose the address would turn a forwarded
 * message into an account.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MEMBER_ROLE_LABELS, type InvitationPreview, type MemberRole } from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { withNext } from '../../lib/intent';
import { AuthLayout } from './AuthLayout';
import { Button, Field, Input, Spinner } from '../../components/ui';

export function AcceptInvitePage(): ReactNode {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { acceptInvitation, selectBusiness, user, logout } = useAuth();

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const invite = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.get<InvitationPreview>(`/auth/invitations/${token}`),
    retry: false,
  });

  if (invite.isLoading) {
    return (
      <AuthLayout title="Checking your invitation">
        <div className="grid place-items-center py-8">
          <Spinner />
        </div>
      </AuthLayout>
    );
  }

  if (invite.isError) {
    const message =
      invite.error instanceof ApiError
        ? invite.error.message
        : 'This invitation link is not valid.';
    return (
      <AuthLayout title="This link cannot be used">
        <p className="text-sm text-slate-600">{message}</p>
        <p className="mt-4 text-sm text-slate-500">
          Ask whoever invited you to send a new link.
        </p>
        <Button className="mt-6 w-full" variant="secondary" onClick={() => navigate('/login')}>
          Go to sign in
        </Button>
      </AuthLayout>
    );
  }

  const preview = invite.data!;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const session = await acceptInvitation(
        token,
        preview.needsAccount ? { fullName, password } : {},
      );
      /**
       * Into the business they were invited to — never into onboarding.
       *
       * `/business` decides between a dashboard and a counter, so a courier
       * accepting an invitation lands at their location rather than in a shop
       * manager's screen. Selecting the invited workspace first means somebody
       * who already belonged elsewhere opens the one they just joined.
       */
      const joined = session.memberships.find((m) => m.businessName === preview.businessName);
      if (joined) selectBusiness(joined.businessId);
      navigate('/business', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not complete your sign-up.');
      setSubmitting(false);
    }
  };

  const roleLabel = MEMBER_ROLE_LABELS[preview.role as MemberRole] ?? preview.role;

  /**
   * An existing account accepts from its own session.
   *
   * The link names the account; it does not prove the reader owns it — the
   * person who sent it holds the same link. So somebody who already uses
   * CenoX signs in first, as the invited address, and comes straight back.
   */
  const here = `/join/${token}`;
  const signedInAsInvitee =
    user != null &&
    typeof preview.email === 'string' &&
    user.email.toLowerCase() === preview.email.toLowerCase();

  if (!preview.needsAccount && !signedInAsInvitee) {
    return (
      <AuthLayout
        title={`Join ${preview.businessName}`}
        subtitle={`You have been invited as ${roleLabel}`}
      >
        <p className="text-sm text-slate-600">
          This invitation is for <strong className="text-slate-900">{preview.email}</strong>,
          which already has a CenoX account.{' '}
          {user
            ? `You are signed in as ${user.email}. Sign out, then sign in as ${preview.email} to accept.`
            : 'Sign in to that account to accept.'}
        </p>
        <Button
          className="mt-6 w-full"
          onClick={() => {
            const go = () => navigate(withNext('/login', here));
            if (user) void logout().then(go);
            else go();
          }}
        >
          {user ? `Sign out and sign in as ${preview.email}` : `Sign in as ${preview.email}`}
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={`Join ${preview.businessName}`}
      subtitle={`You have been invited as ${roleLabel}`}
    >
      <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
        Signing in as <strong className="text-slate-900">{preview.email}</strong>
      </p>

      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </p>
      )}

      <form className="mt-5 space-y-4" onSubmit={submit}>
        {preview.needsAccount ? (
          <>
            <Field label="Your name" required>
              {(id) => (
                <Input
                  id={id}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                  required
                />
              )}
            </Field>
            <Field label="Choose a password" required hint="At least 10 characters">
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              )}
            </Field>
          </>
        ) : (
          <p className="text-sm text-slate-600">
            You already have a CenoX account for this address. Accepting adds{' '}
            {preview.businessName} to it.
          </p>
        )}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Joining…' : `Join ${preview.businessName}`}
        </Button>
      </form>
    </AuthLayout>
  );
}
